import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Crown, Trophy, Gem, Sparkles, Gift, ArrowRight, RefreshCw, Plane, GraduationCap, HeartPulse } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

// Types
interface UserPointsView {
  user_id: string;
  available_points: number;
  lifetime_points: number;
  tier: "silver" | "gold" | "platinum" | "diamond" | string;
  tier_points: number;
  tier_min: number;
  cashback_bps: number;
  perks: any;
  next_tier_min: number | null;
}

interface LedgerRow {
  id: string;
  kind: "earn" | "redeem" | "expire" | "adjust";
  source: string;
  description: string | null;
  points: number;
  points_sign: 1 | -1;
  provider: string | null;
  booking_id: string | null;
  created_at: string;
}

interface RewardItem {
  id: string;
  title: string;
  partner: string | null;
  points_cost: number;
  stock: number | null;
  is_active: boolean;
  meta: any;
}

// Helpers
function cn(...xs: (string | false | undefined | null)[]) {
  return xs.filter(Boolean).join(" ");
}

function tierMeta(tier: string | undefined) {
  switch (tier) {
    case "diamond":
      return { label: "Diamond", icon: Gem, color: "text-[hsl(var(--flexaura-accent))]", bg: "bg-[hsl(var(--flexaura-accent)/0.1)]", border: "border-[hsl(var(--flexaura-accent))]" };
    case "platinum":
      return { label: "Platinum", icon: Trophy, color: "text-muted-foreground", bg: "bg-muted", border: "border-muted-foreground/40" };
    case "gold":
      return { label: "Gold", icon: Crown, color: "text-[hsl(var(--flexaura-accent))]", bg: "bg-[hsl(var(--flexaura-accent)/0.1)]", border: "border-[hsl(var(--flexaura-accent))]" };
    default:
      return { label: "Silver", icon: Sparkles, color: "text-muted-foreground", bg: "bg-muted", border: "border-muted-foreground/30" };
  }
}

function formatDate(dt: string) {
  return new Date(dt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function kindStatus(kind: string) {
  switch (kind) {
    case "earn": return { label: "AWARDED", cls: "text-[hsl(var(--flexaura-accent))]" };
    case "redeem": return { label: "REDEEMED", cls: "text-[hsl(var(--flexaura-success))]" };
    case "expire": return { label: "REVOKED", cls: "text-destructive" };
    default: return { label: "PENDING", cls: "text-muted-foreground" };
  }
}

// Card component
const PCard: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ className, children }) => (
  <div className={cn("rounded-2xl bg-card border border-border shadow-lg p-6", className)}>
    {children}
  </div>
);

const ProgressBar: React.FC<{ value: number; max: number }> = ({ value, max }) => {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <div className="w-full h-3 rounded-full bg-secondary overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, background: "linear-gradient(90deg, hsl(var(--flexaura-primary)), hsl(var(--flexaura-accent)))" }}
      />
    </div>
  );
};

export default function PointsDashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vp, setVp] = useState<UserPointsView | null>(null);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [rewards, setRewards] = useState<RewardItem[]>([]);
  const [redeeming, setRedeeming] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/auth?next=/account/points");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    Promise.all([
      supabase.rpc("get_user_points", { p_user_id: user.id }),
      supabase
        .from("points_ledger")
        .select("id, kind, source, description, points, points_sign, provider, booking_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase.from("rewards").select("*").eq("is_active", true).order("points_cost", { ascending: true }),
    ])
      .then(([vpRes, ledRes, rewRes]) => {
        if (vpRes.data && vpRes.data.length > 0) setVp(vpRes.data[0] as UserPointsView);
        if (ledRes.data) setLedger(ledRes.data as LedgerRow[]);
        if (rewRes.data) setRewards(rewRes.data as RewardItem[]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user?.id]);

  const meta = tierMeta(vp?.tier);
  const progressVal = useMemo(() => {
    if (!vp) return 0;
    const base = vp.tier_min || 0;
    const target = vp.next_tier_min ?? base;
    return Math.min(Math.max(1, target - base), Math.max(0, vp.tier_points - base));
  }, [vp]);

  async function refreshAll() {
    if (!user?.id) return;
    setRefreshing(true);
    const { data: vpData } = await supabase.rpc("get_user_points", { p_user_id: user.id });
    if (vpData && vpData.length > 0) setVp(vpData[0] as UserPointsView);
    const led = await supabase
      .from("points_ledger")
      .select("id, kind, source, description, points, points_sign, provider, booking_id, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);
    if (led.data) setLedger(led.data as LedgerRow[]);
    setRefreshing(false);
  }

  async function handleRedeem(reward: RewardItem) {
    if (!user?.id || !vp) return;
    if (vp.available_points < reward.points_cost) return alert("Not enough points.");
    if (!window.confirm(`Redeem ${reward.points_cost} points for "${reward.title}"?`)) return;
    setRedeeming(reward.id);
    const { data: rr, error: rrErr } = await supabase
      .from("reward_redemptions")
      .insert({ reward_id: reward.id, user_id: user.id, points_spent: reward.points_cost, status: "pending" })
      .select("id")
      .single();
    if (rrErr) { setRedeeming(null); return alert("Could not start redemption."); }
    const { error: rpcErr } = await supabase.rpc("redeem_points", { p_user: user.id, p_points: reward.points_cost, p_desc: `Reward: ${reward.title}` });
    if (rpcErr) {
      await supabase.from("reward_redemptions").update({ status: "failed" }).eq("id", rr.id);
      setRedeeming(null);
      return alert("Could not redeem points.");
    }
    await supabase.from("reward_redemptions").update({ status: "fulfilled" }).eq("id", rr.id);
    await refreshAll();
    setRedeeming(null);
    alert("Redeemed! Check your email for details.");
  }

  // Compute vertical totals from ledger
  const verticalTotals = useMemo(() => {
    const totals = { travel: 0, education: 0, healthcare: 0 };
    for (const row of ledger) {
      if (row.kind !== "earn") continue;
      const src = (row.source || "").toLowerCase();
      const desc = (row.description || "").toLowerCase();
      if (src.includes("travel") || desc.includes("travel") || desc.includes("flight") || desc.includes("hotel")) totals.travel += row.points;
      else if (src.includes("education") || desc.includes("education") || desc.includes("school") || desc.includes("course")) totals.education += row.points;
      else if (src.includes("healthcare") || desc.includes("healthcare") || desc.includes("treatment")) totals.healthcare += row.points;
      else totals.travel += row.points; // default bucket
    }
    return totals;
  }, [ledger]);

  if (authLoading || loading || !vp) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="animate-spin" /> Loading your points…
        </div>
      </div>
    );
  }

  const Icon = meta.icon;
  const canRedeem = vp.available_points >= 500;
  const redemptionValue = Math.floor(vp.available_points * 0.01); // 1pt ≈ AED 0.01

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* ── HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs font-mono tracking-widest uppercase text-muted-foreground mb-1">Flexaura Points</p>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-[hsl(var(--flexaura-accent))]">
              {vp.available_points.toLocaleString()}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Points = 1% of transaction value. Redeem for fee discounts on future bookings.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Tier badge */}
            <div className={cn("inline-flex items-center gap-2 px-4 py-2 rounded-full border", meta.bg, meta.border)}>
              <Icon className={cn("w-4 h-4", meta.color)} />
              <span className={cn("text-sm font-semibold", meta.color)}>{meta.label}</span>
            </div>
            <button
              onClick={refreshAll}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors text-sm"
            >
              <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} /> Refresh
            </button>
          </div>
        </div>

        {/* ── EARNING SUMMARY (3 cards) ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {[
            { label: "Travel Bookings", icon: Plane, pts: verticalTotals.travel },
            { label: "Education Bookings", icon: GraduationCap, pts: verticalTotals.education },
            { label: "Healthcare Bookings", icon: HeartPulse, pts: verticalTotals.healthcare },
          ].map((v) => (
            <PCard key={v.label}>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-[hsl(var(--flexaura-primary)/0.1)]">
                  <v.icon className="w-5 h-5 text-[hsl(var(--flexaura-primary))]" />
                </div>
                <span className="text-sm text-muted-foreground">{v.label}</span>
              </div>
              <div className="text-2xl font-bold font-mono text-[hsl(var(--flexaura-accent))]">
                {v.pts.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">pts</span>
              </div>
            </PCard>
          ))}
        </div>

        {/* ── TIER PROGRESS ── */}
        {vp.next_tier_min && (
          <PCard className="mb-10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Progress to next tier</span>
              <span className="text-sm font-mono font-medium">
                {progressVal.toLocaleString()} / {((vp.next_tier_min || 0) - (vp.tier_min || 0)).toLocaleString()}
              </span>
            </div>
            <ProgressBar value={progressVal} max={(vp.next_tier_min - vp.tier_min) || 1} />
          </PCard>
        )}

        {/* ── TRANSACTION HISTORY + REDEMPTION ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Table */}
          <div className="lg:col-span-2">
            <PCard>
              <h2 className="text-xl font-heading font-semibold mb-4">Transaction History</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-muted-foreground border-b border-border">
                    <tr>
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Vertical</th>
                      <th className="py-2 pr-4">Provider</th>
                      <th className="py-2 pr-4 text-right">Points</th>
                      <th className="py-2 pr-4 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-10 text-center text-muted-foreground">
                          Your points will appear here after your first booking is confirmed.
                        </td>
                      </tr>
                    )}
                    {ledger.map((row) => {
                      const status = kindStatus(row.kind);
                      return (
                        <tr key={row.id} className="border-b border-border/50">
                          <td className="py-3 pr-4 whitespace-nowrap font-mono text-xs">{formatDate(row.created_at)}</td>
                          <td className="py-3 pr-4 capitalize text-xs">{row.source}</td>
                          <td className="py-3 pr-4 capitalize text-xs">{row.provider ?? "—"}</td>
                          <td className={cn(
                            "py-3 pr-4 text-right font-mono font-medium",
                            row.points_sign === 1 ? "text-[hsl(var(--flexaura-accent))]" : "text-destructive"
                          )}>
                            {row.points_sign === 1 ? "+" : "−"}{row.points.toLocaleString()}
                          </td>
                          <td className={cn("py-3 text-right font-mono text-xs font-semibold", status.cls)}>
                            {status.label}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </PCard>
          </div>

          {/* Redemption + Rewards */}
          <div className="lg:col-span-1 space-y-6">
            {/* Redemption calculator */}
            <PCard>
              <h2 className="text-lg font-heading font-semibold mb-3">Redeem Points</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Available</span>
                  <span className="font-mono font-medium text-[hsl(var(--flexaura-accent))]">{vp.available_points.toLocaleString()} pts</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Min. redemption</span>
                  <span className="font-mono">500 pts</span>
                </div>
                <div className="flex justify-between text-sm border-t border-border pt-3">
                  <span className="text-muted-foreground">Discount value</span>
                  <span className="font-mono font-medium">AED {redemptionValue.toLocaleString()}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {vp.available_points.toLocaleString()} pts = AED {redemptionValue.toLocaleString()} discount
                </p>
                <button
                  disabled={!canRedeem}
                  className={cn(
                    "w-full py-2.5 rounded-xl font-medium text-sm transition-colors",
                    canRedeem
                      ? "bg-[hsl(var(--flexaura-primary))] text-white hover:bg-[hsl(var(--flexaura-primary)/0.9)]"
                      : "bg-secondary text-muted-foreground cursor-not-allowed"
                  )}
                >
                  {canRedeem ? "Redeem Points" : "Need 500+ pts to redeem"}
                </button>
              </div>
            </PCard>

            {/* Rewards catalogue */}
            <PCard>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <Gift className="w-4 h-4" /> Rewards
                </h2>
              </div>
              <div className="space-y-3">
                {rewards.slice(0, 5).map((rw) => (
                  <div key={rw.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/50 border border-border">
                    <div>
                      <div className="font-medium text-sm">{rw.title}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {rw.points_cost.toLocaleString()} pts
                      </div>
                    </div>
                    <button
                      onClick={() => handleRedeem(rw)}
                      disabled={redeeming === rw.id || vp.available_points < rw.points_cost}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                        vp.available_points >= rw.points_cost
                          ? "bg-[hsl(var(--flexaura-primary))] text-white hover:bg-[hsl(var(--flexaura-primary)/0.9)]"
                          : "bg-secondary cursor-not-allowed opacity-50"
                      )}
                    >
                      {redeeming === rw.id ? "…" : "Redeem"}
                    </button>
                  </div>
                ))}
                {rewards.length === 0 && (
                  <div className="text-muted-foreground text-sm text-center py-4">No rewards available yet.</div>
                )}
              </div>
            </PCard>
          </div>
        </div>
      </div>
    </div>
  );
}
